import React, { Children } from 'react';
import { motion } from 'framer-motion';
export const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.5,
  delay,
  ease: [0.21, 0.47, 0.32, 0.98]
}} className={className} data-id="element-100">
    {children}
  </motion.div>;
export const StaggerContainer = ({
  children,
  className = ''
}: {
  children: ReactNode;
  className?: string;
}) => <motion.div initial="hidden" whileInView="visible" viewport={{
  once: true,
  margin: '-100px'
}} variants={{
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
}} className={className} data-id="element-101">
    {children}
  </motion.div>;
export const StaggerItem = ({
  children,
  className = ''
}: {
  children: ReactNode;
  className?: string;
}) => <motion.div variants={{
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.21, 0.47, 0.32, 0.98]
    }
  }
}} className={className} data-id="element-102">
    {children}
  </motion.div>;
export const Slide = ({
  children,
  id,
  className = '',
  bg = 'white'
}: {
  children: ReactNode;
  id: string;
  className?: string;
  bg?: 'white' | 'gray' | 'black';
}) => {
  const bgClass = bg === 'gray' ? 'bg-slate-50' : bg === 'black' ? 'bg-black text-white' : 'bg-white';
  return <section id={id} className={`min-h-screen flex flex-col justify-center py-24 px-6 md:px-12 lg:px-24 w-full relative ${bgClass} ${className}`} data-id="element-103">
      <div className="max-w-7xl mx-auto w-full" data-id="element-104">{children}</div>
    </section>;
};
export const SlideHeading = ({
  title,
  subtitle,
  align = 'left',
  tag,
  dark = false
}: {
  title: string;
  subtitle?: string;
  align?: 'left' | 'center';
  tag?: string;
  dark?: boolean;
}) => <FadeIn className={`mb-16 ${align === 'center' ? 'flex flex-col items-center text-center' : ''}`} data-id="element-105">
    {tag && <div className={`inline-block text-xs md:text-sm font-bold tracking-widest px-4 py-2 mb-6 uppercase ${dark ? 'bg-white text-black' : 'bg-black text-white'}`} data-id="element-106">
        {tag}
      </div>}
    <h2 className={`text-5xl md:text-6xl lg:text-7xl font-serif font-bold mb-6 leading-none uppercase ${dark ? 'text-white' : 'text-black'}`} data-id="element-107">
      {title}
    </h2>
    {subtitle && <p className={`text-xl md:text-2xl font-medium max-w-3xl ${dark ? 'text-slate-300' : 'text-slate-600'}`} data-id="element-108">
        {subtitle}
      </p>}
    <div className={`h-1.5 w-24 mt-8 ${dark ? 'bg-white' : 'bg-black'} ${align === 'center' ? 'mx-auto' : ''}`} data-id="element-109" />
  </FadeIn>;
export const Card = ({
  children,
  className = '',
  dark = false
}: {
  children: ReactNode;
  className?: string;
  dark?: boolean;
}) => <div className={`${dark ? 'bg-black text-white border-black' : 'bg-white text-black border-black'} border-2 p-8 hover:-translate-y-1 transition-transform duration-300 ${className}`} data-id="element-110">
    {children}
  </div>;